// @tennis-matches/services/tournament-wizard/validation
// React hook for wizard validation

import { useMemo } from "react";
import { createValidationRules } from "./validationRules.js";
import { __asStr } from "@tennis-matches/core/utils";
import { isTournamentStartInPast } from "../../tournament/tournamentStartDateTime.js";

function __periodsOk(periods, tournamentMode) {
  const p = periods && typeof periods === "object" ? periods : {};
  if (tournamentMode === "groupsKo") {
    return (
      __asStr(p.groups?.startDate).trim() !== "" &&
      __asStr(p.groups?.endDate).trim() !== "" &&
      __asStr(p.ko?.startDate).trim() !== "" &&
      __asStr(p.ko?.endDate).trim() !== ""
    );
  }
  // K.O.-only: PeriodsStep hat nur optionalen Gesamtzeitraum
  return true;
}

function __detailsOk(setup, rules) {
  const mode = __asStr(setup.tournamentMode || "groupsKo");
  if (mode === "groupsKo") {
    return rules.validateGroupsKo ? rules.validateGroupsKo(setup).ok : true;
  }
  if (mode === "ko") {
    return rules.validateKoOnly ? rules.validateKoOnly(setup).ok : true;
  }
  if (mode === "swiss") return false;
  return true;
}

/**
 * React hook for tournament wizard validation
 * @param {Object} setup - Setup data object
 * @param {Object} config - Validation configuration
 * @param {Function} t - Optional translation function
 * @returns {{ validation: Object, stepOk: Object }}
 */
export function useWizardValidation(setup, config, t = null) {
  const rules = useMemo(() => createValidationRules(config, t), [config, t]);

  const validation = useMemo(() => {
    const lines = [];
    const warnings = [];
    const currentMode = setup.wizardMode || "simple";
    const modeConfig = config.modes?.[currentMode];
    const tournamentMode = __asStr(setup.tournamentMode || "groupsKo");

    // Validate meta (always)
    const metaResult = rules.validateMeta(setup);
    if (!metaResult.ok) {
      lines.push(...metaResult.messages);
    }
    warnings.push(...metaResult.warnings);

    // Validate players
    const playersResult = rules.validatePlayers(setup);
    if (!playersResult.ok) {
      lines.push(...playersResult.messages);
    }
    warnings.push(...playersResult.warnings);

    // Validate time (only when time step is used; Clubmeisterschaft uses periods instead)
    const hasTimeStep = modeConfig?.steps?.includes("time");
    if (hasTimeStep) {
      const timeResult = rules.validateTime(setup);
      if (!timeResult.ok) {
        lines.push(...timeResult.messages);
      }
      warnings.push(...timeResult.warnings);
    }

    // Validate rules (only if rules step is enabled in current mode)
    const hasRulesStep = modeConfig?.steps?.includes("rules");

    if (hasRulesStep) {
      const rulesResult = rules.validateRules(setup);
      if (!rulesResult.ok) {
        lines.push(...rulesResult.messages);
      }
      warnings.push(...rulesResult.warnings);
    }

    const hasGroupsKoStep = modeConfig?.steps?.includes("groupsko");
    const hasDetailsStep = modeConfig?.steps?.includes("details");
    const shouldValidateGroupsKo =
      (hasGroupsKoStep || (hasDetailsStep && tournamentMode === "groupsKo")) &&
      rules.validateGroupsKo;
    if (shouldValidateGroupsKo) {
      const groupsKoResult = rules.validateGroupsKo(setup);
      if (!groupsKoResult.ok) {
        lines.push(...(groupsKoResult.messages || []));
      }
    }

    const shouldValidateKoOnly =
      (modeConfig?.steps?.includes("ko") ||
        (hasDetailsStep && tournamentMode === "ko")) &&
      rules.validateKoOnly;
    if (shouldValidateKoOnly) {
      const koResult = rules.validateKoOnly(setup);
      if (!koResult.ok) {
        lines.push(...(koResult.messages || []));
      }
    }

    return {
      ok: lines.length === 0,
      lines,
      warnings,
    };
  }, [setup, rules, config]);

  // Step-specific validation
  const stepOk = useMemo(() => {
    const { startDate, startTime, format } = setup;
    const currentMode = setup.wizardMode || "simple";
    const modeConfig = config.modes?.[currentMode];
    const tournamentMode = __asStr(setup.tournamentMode || "groupsKo");

    // Meta validation: when "time" step is used, require date+time; when "periods" (Clubmeisterschaft), only format
    const hasTimeStep = modeConfig?.steps?.includes("time");
    const okMeta =
      !!__asStr(format) &&
      (hasTimeStep
        ? !!__asStr(startDate) &&
          (() => {
            const s = String(startTime || "").trim();
            const m = s.match(/^(\d{1,2}):(\d{2})$/);
            return m != null && !isTournamentStartInPast(startDate, startTime);
          })()
        : true);

    // Players validation
    const playersResult = rules.validatePlayers(setup);
    const okPlayers = playersResult.ok;

    // Time validation
    const timeResult = rules.validateTime(setup);
    const okTime = timeResult.ok;

    // Rules validation (only if rules step is enabled)
    const hasRulesStep = modeConfig?.steps?.includes("rules");
    const okRules = hasRulesStep ? rules.validateRules(setup).ok : true;
    // Rules_cm (Clubmeisterschaft): no preset validation, step always ok for now
    const hasRulesCmStep = modeConfig?.steps?.includes("rules_cm");
    const okRulesCm = !hasRulesCmStep || true;

    // GroupsKo validation (legacy step id "groupsko")
    const hasGroupsKoStep = modeConfig?.steps?.includes("groupsko");
    const okGroupsKo =
      hasGroupsKoStep && rules.validateGroupsKo
        ? rules.validateGroupsKo(setup).ok
        : true;
    // ko / swiss placeholder steps (Clubmeisterschaft)
    const hasKoStep = modeConfig?.steps?.includes("ko");
    const hasSwissStep = modeConfig?.steps?.includes("swiss");
    const okKo =
      !hasKoStep ||
      (rules.validateKoOnly ? rules.validateKoOnly(setup).ok : true);
    const okSwiss = !hasSwissStep || true;

    // Details step (Clubmeisterschaft: groupsKo / ko / swiss je nach Modus)
    const hasDetailsStep = modeConfig?.steps?.includes("details");
    const okDetails = !hasDetailsStep || __detailsOk(setup, rules);

    // Mode validation (only if mode step is enabled – Clubmeisterschaft)
    const hasModeStep = modeConfig?.steps?.includes("mode");
    const okMode =
      !hasModeStep ||
      tournamentMode === "ko" ||
      tournamentMode === "groupsKo" ||
      tournamentMode === "swiss";

    // Periods validation (only if periods step is enabled – Clubmeisterschaft)
    const hasPeriodsStep = modeConfig?.steps?.includes("periods");
    const okPeriods =
      !hasPeriodsStep || __periodsOk(setup.periods, tournamentMode);

    // Review validation: overall validation must be ok
    const okReview = validation.ok;

    return {
      meta: okMeta,
      players: okPlayers,
      mode: okMode,
      details: okDetails,
      groupsko: okGroupsKo,
      ko: okKo,
      swiss: okSwiss,
      periods: okPeriods,
      time: okTime,
      rules: okRules,
      rules_cm: okRulesCm,
      review: okReview,
    };
  }, [setup, rules, config, validation.ok]);

  return {
    validation,
    stepOk,
  };
}
